'use client';

import React from 'react';
import ProductCarousel from '@/components/ui/ProductCarousel';
import { RecipeRelatedProduct } from '@/types/recipe';
import { ProductBase } from '@/types/product';
import { BreadcrumbMenu } from '@/types/menu';

interface RelatedRecipeProductsCarouselProps {
  products: RecipeRelatedProduct[];
  className?: string;
  title?: string;
  subtitle?: string;
  /** Ajustes de tipografía y carrusel para vista móvil */
  isMobile?: boolean;
}

const recipeBreadcrumb: BreadcrumbMenu = {
  menuLevel1: {
    id: -1,
    name: 'Recetas',
    description: 'Recetas',
    level: 1,
    parent: 0,
    slug: 'recetas',
    pageNumbers: 1,
    url: '/recetas',
  },
};

const toProductCardModel = (product: RecipeRelatedProduct): ProductBase => {
  const priceFinalInc = Number(product.price ?? 0);
  const priceOriginalInc = Number(product.originalPrice ?? priceFinalInc);
  const discountInc =
    priceOriginalInc > priceFinalInc ? Math.max(0, priceOriginalInc - priceFinalInc) : 0;

  return {
    id: product.id,
    type: 'recipe-related',
    reference: '',
    barcode: '',
    title: product.title,
    brandId: 0,
    brand: product.brand || 'Hiperalia',
    priceFinalInc,
    priceFinalExc: priceFinalInc,
    priceOriginalInc,
    priceOriginalExc: priceOriginalInc,
    discountInc,
    discountExc: discountInc,
    weight: '',
    shortDescription: '',
    description: '',
    image: product.image,
    images: [{ id: 0, src: product.image, alt: product.title, isMain: true }],
    thumbnails: [],
    id_manufacturer: 0,
    isOffer: discountInc > 0,
    isGlutenFree: false,
    slug: product.slug,
    categoriesIds: [],
    productFeaturesIds: [],
    productCombinationsIds: [],
    productOptionsIds: [],
    categoryId: 0,
    subCategoryId: 0,
    subSubCategoryId: 0,
    url: `/${product.slug}`,
    metaDescription: product.title,
    metaTitle: product.title,
    breadcrumb: [{ id: -1, parentId: 0, name: 'Recetas', slug: 'recetas' }],
  };
};

const RelatedRecipeProductsCarousel: React.FC<RelatedRecipeProductsCarouselProps> = ({
  products,
  className = '',
  title = 'QUIZA TAMBIEN TE GUSTE...',
  subtitle = 'Seguro que te interesa',
  isMobile = false,
}) => {
  if (products.length === 0) {
    return null;
  }

  const items = products.map(toProductCardModel);

  return (
    <section data-component-id="src/components/sections/recipes/RelatedRecipeProductsCarousel.tsx:89:4" data-component-path="src/components/sections/recipes/RelatedRecipeProductsCarousel.tsx" data-component-line="89" data-component-file="RelatedRecipeProductsCarousel.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%7D" className={`${isMobile ? 'py-8' : 'py-12'} ${className}`}>
      <div data-component-id="src/components/sections/recipes/RelatedRecipeProductsCarousel.tsx:90:6" data-component-path="src/components/sections/recipes/RelatedRecipeProductsCarousel.tsx" data-component-line="90" data-component-file="RelatedRecipeProductsCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`mb-2 text-left ${isMobile ? 'px-4' : ''}`}>
        <h2 data-component-id="src/components/sections/recipes/RelatedRecipeProductsCarousel.tsx:91:8" data-component-path="src/components/sections/recipes/RelatedRecipeProductsCarousel.tsx" data-component-line="91" data-component-file="RelatedRecipeProductsCarousel.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%7D"
          className={`mb-1 uppercase text-global-1 ${
            isMobile ? 'text-[22px] leading-[28px]' : 'text-[40px] leading-[48px]'
          }`}
          style={{
            fontFamily: "'TT_Norms_Pro-Extra-Black', Helvetica",
            fontWeight: '900',
          }}
        >
          {title}
        </h2>
        <div data-component-id="src/components/sections/recipes/RelatedRecipeProductsCarousel.tsx:102:8" data-component-path="src/components/sections/recipes/RelatedRecipeProductsCarousel.tsx" data-component-line="102" data-component-file="RelatedRecipeProductsCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`w-fit rounded-none bg-global-9 ${isMobile ? 'mb-4 px-3 py-0.5' : 'mb-6 px-4'}`}>
          <span data-component-id="src/components/sections/recipes/RelatedRecipeProductsCarousel.tsx:103:10" data-component-path="src/components/sections/recipes/RelatedRecipeProductsCarousel.tsx" data-component-line="103" data-component-file="RelatedRecipeProductsCarousel.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
            className={`text-global-1 ${isMobile ? 'text-sm leading-4' : 'text-[22px] leading-[27px]'}`}
            style={{
              fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
              fontWeight: '400',
            }}
          >
            {subtitle}
          </span>
        </div>
        <ProductCarousel data-component-id="src/components/sections/recipes/RelatedRecipeProductsCarousel.tsx:113:8" data-component-path="src/components/sections/recipes/RelatedRecipeProductsCarousel.tsx" data-component-line="113" data-component-file="RelatedRecipeProductsCarousel.tsx" data-component-name="ProductCarousel" data-component-content="%7B%22elementName%22%3A%22ProductCarousel%22%7D"
          breadcrumbMenu={recipeBreadcrumb}
          items={items}
          showNavigation
          defaultVisibleCount={isMobile ? 2 : 5}
          productTitleAsParagraph
        />
      </div>
    </section>
  );
};

export default RelatedRecipeProductsCarousel;
